import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Lock, Mail, Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { adminLogin } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useEffect } from 'react';

const loginSchema = z.object({
    email: z.string().email('Please enter a valid email address'),
    password: z.string().min(6, 'Password must be at least 6 characters')
});

const AdminLoginPage = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    // Add noindex meta tag for admin pages
    useEffect(() => {
        const meta = document.createElement('meta');
        meta.name = 'robots';
        meta.content = 'noindex, nofollow';
        document.head.appendChild(meta);
        return () => {
            document.head.removeChild(meta);
        };
    }, []);

    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm({
        resolver: zodResolver(loginSchema)
    });

    const onSubmit = async (data) => {
        setIsLoading(true);
        setError(null);

        try {
            await adminLogin(data.email, data.password);
            navigate('/admin');
        } catch (err) {
            setError(err.message || 'Invalid email or password');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-primary flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-primary-foreground/10 border-2 border-primary-foreground/30 mb-4">
                        <span className="font-display font-bold text-3xl text-primary-foreground">
                            GM
                        </span>
                    </div>
                    <h1 className="font-display text-3xl font-bold text-primary-foreground">
                        Admin Panel
                    </h1>
                    <p className="text-primary-foreground/70 mt-2">
                        Girne MUN 2026 Management System
                    </p>
                </div>

                {/* Login Form */}
                <div className="bg-card rounded-xl border border-border p-8 shadow-elegant-lg">
                    <h2 className="font-display text-xl font-semibold text-foreground mb-6 text-center">
                        Sign in to your account
                    </h2>

                    {error && (
                        <div className="flex items-center gap-3 p-4 rounded-lg bg-destructive/10 border border-destructive/20 mb-6">
                            <AlertCircle className="w-5 h-5 text-destructive shrink-0" />
                            <p className="text-destructive text-sm">{error}</p>
                        </div>
                    )}

                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
                        {/* Email */}
                        <div>
                            <Label htmlFor="email" className="text-foreground font-medium">
                                Email Address
                            </Label>
                            <div className="relative mt-2">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                <Input
                                    id="email"
                                    type="email"
                                    {...register('email')}
                                    className={cn(
                                        "pl-10 input-elegant",
                                        errors.email && "border-destructive"
                                    )}
                                    placeholder="admin@girnemun.com"
                                />
                            </div>
                            {errors.email && (
                                <p className="text-destructive text-sm mt-1">{errors.email.message}</p>
                            )}
                        </div>

                        {/* Password */}
                        <div>
                            <Label htmlFor="password" className="text-foreground font-medium">
                                Password
                            </Label>
                            <div className="relative mt-2">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                                <Input
                                    id="password"
                                    type="password"
                                    {...register('password')}
                                    className={cn(
                                        "pl-10 input-elegant",
                                        errors.password && "border-destructive"
                                    )}
                                    placeholder="••••••••"
                                />
                            </div>
                            {errors.password && (
                                <p className="text-destructive text-sm mt-1">{errors.password.message}</p>
                            )}
                        </div>

                        {/* Submit Button */}
                        <Button
                            type="submit"
                            disabled={isLoading}
                            className="w-full btn-navy py-6 font-semibold"
                        >
                            {isLoading ? (
                                <span className="flex items-center justify-center gap-2">
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    Signing in...
                                </span>
                            ) : (
                                'Sign In'
                            )}
                        </Button>
                    </form>

                    {/* Admin Credentials Info */}
                    <div className="mt-6 pt-6 border-t border-border">
                        <p className="text-muted-foreground text-sm text-center mb-3">
                            Admin Access
                        </p>
                        <div className="bg-muted rounded-lg p-3 text-sm">
                            <p className="text-muted-foreground">
                                <span className="font-medium text-foreground">Email:</span> admin@girnemun.com
                            </p>
                            <p className="text-muted-foreground mt-1">
                                <span className="font-medium text-foreground">Password:</span> Contact administrator
                            </p>
                        </div>
                    </div>
                </div>

                {/* Back to site */}
                <p className="text-center mt-6">
                    <a href="/" className="text-primary-foreground/70 hover:text-primary-foreground transition-colors text-sm">
                        ← Back to main site
                    </a>
                </p>
            </div>
        </div>
    );
};

export default AdminLoginPage;
